import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { List } from 'immutable';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';

import { translate as t } from '../../../../lib/I18n';
import { sortFaresBySplitSection } from '../../../../utils/splits';
import OrderFare from '../../../../records/order/Fare';
import Split from '../../../../records/Split';

import FareShort from '../../../checkout/FareShort';
import FareStations from '../../../checkout/FareStations';
import DateTime from '../../../general/DateTime';
import Icon from '../../../general/Icon';
import AmendedSplitFaresModal from './AmendedSplitFaresModal';

const renderFareDate = (text, journey, directionIcon) => {
    if (!journey?.legs?.size) {
        return null;
    }
    return (
        <div className="text-sm clearfix">
            <p className="direction no-margin text-light text-weight--normal">
                <Icon icon="inbound" className={ directionIcon + ' text-light text-primary' } />
                { t(text) + ' ' }
                <DateTime
                    className="text-primary"
                    dateTime={ journey.getDepartureTime() }
                    format="HH:mm ddd DD MMM YYYY"
                />
            </p>
        </div>
    );
};

function AmendedSplitFares({ fares, showJourneyDates }) {
    const [showModal, setShowModal] = useState(false);

    if (!fares.length) {
        return null;
    }

    if (fares.length === 1) {
        return <FareShort orderFare={ fares[0] } />;
    }

    const sortedFares = sortFaresBySplitSection(fares);
    const firstFare = sortedFares[0];
    const lastFare = sortedFares[sortedFares.length - 1];
    // Split is duck-typed against OrderFare here — only isAdvance()/isFlexi()/isReturn()/origin/destination
    // are implemented on both. Split.isDiscounted()/getMinSeatAvailability() are not, and would throw/no-op.
    const split = new Split({ fares: new List(sortedFares) });
    const ticketType = split.getTicketType();
    const ticketTypeName = ticketType ? t(`ticket.split.type.${ticketType}`) : '';

    return (
        <li className="order-summary__item order-summary__item--short margin-bottom amended-split-fares">
            <Row className="fare-summary">
                <Col xs={ 12 } md={ 6 } lg={ 6 }>
                    <FareStations
                        origin={ split.getOrigin() }
                        destination={ split.getDestination() }
                        isReturn={ split.isReturn() }
                    />
                </Col>
                <Col xs={ 12 } md={ 6 } lg={ 6 } className="text-weight--normal ticket-type no-margin">
                    <p className="text-weight--normal ticket-type no-margin amended-split-fares__ticket-type">
                        <Icon icon="splitticketshort" className="split-ticket-icon"/>
                        { ticketTypeName }
                        <button
                            type="button"
                            className="amended-split-fares__info-btn"
                            aria-label={ t('booking.fares.details') }
                            onClick={ () => setShowModal(true) }
                        >
                            <Icon icon="addreservationinfo" className="split-ticket-info-icon" />
                        </button>
                    </p>
                </Col>
            </Row>

            {/* First leg's outward / last leg's return, matching the convention in utils/splits.js's
                getMergedSplitJourneys — don't swap these without checking that logic first. */}
            { showJourneyDates && renderFareDate('order.summary.out', firstFare.outwardJourney, 'otrl-rotate-180') }
            { showJourneyDates && renderFareDate('order.summary.return', lastFare.returnJourney, null) }

            <AmendedSplitFaresModal
                fares={ sortedFares }
                ticketTypeName={ ticketTypeName }
                show={ showModal }
                onClose={ () => setShowModal(false) }
            />
        </li>
    );
}

AmendedSplitFares.propTypes = {
    fares: PropTypes.arrayOf(PropTypes.instanceOf(OrderFare)).isRequired,
    showJourneyDates: PropTypes.bool,
};

AmendedSplitFares.defaultProps = {
    showJourneyDates: true,
};

export default AmendedSplitFares;
